import React, { useEffect, useState } from 'react';
import { Clock, Flame } from 'lucide-react';
import useOrganizationMemberActivityStore from '@/stores/AdminStore/OrganizationMemberActivityStore';
import { LoadingSpinner } from '@/Components/common';

interface Props { userId: string; }

const HoursTab: React.FC<Props> = ({ userId }) => {
  const { hours, overview, loadingHours, fetchHours } = useOrganizationMemberActivityStore();
  const [range, setRange] = useState<number>(30);

  useEffect(() => { fetchHours(userId, range); }, [userId, range, fetchHours]);

  if (loadingHours || !hours) {
    return <div style={{ textAlign: 'center', padding: 32 }}><LoadingSpinner size="md" /></div>;
  }

  const maxHours = Math.max(1, ...hours.daily.map((d) => Number(d.hours || 0)));
  const streak = overview?.stats?.learning_streak ?? 0;
  const monthlyHours = overview?.stats?.current_monthly_hours ?? 0;
  const monthlyGoal = overview?.stats?.monthly_goal_hours ?? 0;

  return (
    <div>
      <div className="omact-toolbar">
        <h3 style={{ margin: 0, fontSize: 16 }}>Learning hours</h3>
        <select
          value={range}
          onChange={(e) => setRange(Number(e.target.value))}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8 }}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div className="omact-stat-card" style={{ flex: 1 }}>
          <div className="omact-stat-label" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Clock size={12} /> Total hours ({range}d)
          </div>
          <div className="omact-stat-value">{Math.round(hours.totalHours * 10) / 10}</div>
        </div>
        <div className="omact-stat-card" style={{ flex: 1 }}>
          <div className="omact-stat-label" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Flame size={12} /> Current streak
          </div>
          <div className="omact-stat-value">{streak} day{streak === 1 ? '' : 's'}</div>
        </div>
        <div className="omact-stat-card" style={{ flex: 1 }}>
          <div className="omact-stat-label">Monthly hours</div>
          <div className="omact-stat-value">
            {Math.round(Number(monthlyHours) * 10) / 10}
            {monthlyGoal ? <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 400 }}> / {monthlyGoal}</span> : null}
          </div>
        </div>
      </div>

      <div className="omact-hours-chart" title="Daily learning hours">
        {hours.daily.map((d) => {
          const h = Number(d.hours || 0);
          const heightPct = (h / maxHours) * 100;
          return (
            <div
              key={d.day}
              className="omact-hours-bar"
              style={{ height: `${heightPct}%` }}
              title={`${d.day}: ${h} hr`}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
        <span>{hours.daily[0]?.day ?? ''}</span>
        <span>{hours.daily[hours.daily.length - 1]?.day ?? ''}</span>
      </div>
    </div>
  );
};

export default HoursTab;
